import _ from 'lodash'
import React from 'react'
import Promise from 'bluebird'
import queryString from 'query-string'

import { browserHistory } from "browser/history"
import { Position, Toast } from 'browser/components/atomic-elements/atoms/toast/toast'

import { Icon } from '@blueprintjs/core'
import { IconNames } from '@blueprintjs/icons'
import apis from '../models/apis'
import { Entity } from 'shared-libs/models/entity'
import { EntityFormPanel } from 'browser/components/atomic-elements/organisms/entity/entity-form-panel'
import { creationSourceFromWeb } from '../utils/utils'
import { ConfirmationModal } from 'browser/components/atomic-elements/organisms/confirmation-modal'
import { Store } from 'shared-libs/models/store'

export const enum NavigationPreference {
  KEEP = 'keep',
  REMOVE = 'remove',
}

// null missing results, annoying interface
function n(indexResult) {
  return indexResult === -1 ? null : indexResult
}

export const navigateTo = (
  entityId: string | undefined = undefined,
  viewId: string = NavigationPreference.KEEP,
  args: any
) => {
  if (!entityId && !viewId) {
    return
  }

  let viewPath = ''
  if (viewId === NavigationPreference.REMOVE) {
    // no-op, default value
  } else if (viewId === NavigationPreference.KEEP) {
    const urlSegments = window.location.pathname.split('/').splice(1)
    // the one time null coalescing is incredible -- expected "0" values
    const lastViewIndex = n(_.findLastIndex(urlSegments, s => s === 'custom')) ?? n(_.findLastIndex(urlSegments, s => s === 'view'))
      ?? n(_.findLastIndex(urlSegments, s => s === 'search'))
    const stopIndex = n(_.findLastIndex(urlSegments, s => s === 'search')) != null ? lastViewIndex + 1 : lastViewIndex + 2
    viewPath = lastViewIndex !== null ? '/' + urlSegments.slice(0, stopIndex).join('/') : ''
  } else {
    viewPath = `/view/${viewId}`
  }

  let entityPath = ''
  if (entityId !== undefined) {
    entityPath = `/entity/${entityId}`
  }

  const destinationPath = `${viewPath}${entityPath}`

  return browserHistory.push({
    pathname: destinationPath,
    search: window.location.search
  })
}

export const showToast = (message: React.ReactNode, delay: number = 3000) => {
  const toast = Toast.show({
    message: (
      <div className="c-toastContents">
        <Icon
          className="c-toastContents-icon mr3"
          icon={IconNames.INFO_SIGN}
          size={30}

        />
        <div className="c-toastContents-message">
          {message}
        </div>
      </div>
    ),
    position: Position.BOTTOM_RIGHT,
    isLightMode: true,
  })

  return new Promise((resolve) => {
    setTimeout(() => {
      Toast.dismiss(toast)
      resolve()
    }, delay)
  })
}

export const showConfirmationModal = (
  options: { title?: string; message?: string; yesText?: string; noText?: string } = {}
): Promise<boolean> => {
  return new Promise<boolean>((resolve) => {
    const finish = _.once(resolve)

    ConfirmationModal.open({
      confirmationTitle: options.title ?? 'Confirm Action',
      confirmationText: options.message ?? 'Are you sure you want to continue?',
      primaryButtonText: options.yesText ?? 'Confirm',
      cancelButtonText: options.noText ?? 'Cancel',
      onPrimaryClicked: () => finish(true),
      onClose: () => finish(false),
    })
  })
}


function getEntitySchema(entitySchema: string | object | undefined) {
  if (!entitySchema) {
    return
  }

  const mappedEntity = _.isString(entitySchema) ? apis.getStore().getRecord(entitySchema) : entitySchema
  return (mappedEntity instanceof Entity) ? mappedEntity : new Entity(mappedEntity, apis)
}

export const showEntityCreationModal = ({ defaultValue, schema }) => {
  const settings = apis.getSettings()
  schema = getEntitySchema(schema)

  const defaultValueWithSource = _.merge(
    { creationSource: creationSourceFromWeb('Table Actions') },
    defaultValue,
  )

  return EntityFormPanel.openAndAwaitSave({
    defaultValue: defaultValueWithSource,
    schema,
    settings,
  })
}

const localRegistrations = new Set()
export const registerFeatureAccess = async () => {
  /*
    /settings/view/b7f9bee2-caeb-49f4-bae5-66680d7c0daf?registerAccess=NjAzNzgxMWEtMTI1OS00MThlLWJkMTUtMTVmMTQwODdhYTI4
      ^^^ ??? ^^^
    /settings/view/b7f9bee2-caeb-49f4-bae5-66680d7c0daf?registerAccess=N2VlMGJlNjAtMjVhZi00NTY2LWE4MTUtOWIxMGZmMzY2YTc3
      ^^^ webster ^^^
  */
  const search = queryString.parse(window.location.search)
  const base64Key = _.get(search, 'registerAccess')

  if (_.isNil(base64Key) || localRegistrations.has(base64Key)) {
    return
  }

  localRegistrations.add(base64Key)
  const facilityId = atob(base64Key)

  const settings = apis.getSettings()
  const legalName = settings.getFirm().business.legalName
  const dotNumber = _.find(settings.getFirm().firm.companyCodes, code => code.division === 'USDOT')

  if (!_.isNil(facilityId) && !_.isNil(legalName) && !_.isNil(dotNumber)) {
    const schedulerAccessJson = await apis.claimSchedulerAccess(facilityId, legalName, dotNumber.code)
    const schedulerAccessEntity = await apis.getStore().addRecord(schedulerAccessJson)

    apis.getStore().emitAll(Store.RECORD_CREATED, schedulerAccessEntity)

    setTimeout(() => showToast('Access Registration Complete.'), 200)
  } else {
    // n.b. toasts won't show while the page is initially loading, which is when this action runs... another bug to fix...
    setTimeout(() => showToast('Error: Cannot register feature access. Please contact an administrator for assistance.'), 200)
  }
}

/**
 * Renotify a driver with a new preauthenticated pre-check deeplink, given an
 * identity and context (e.g. cac uniqueId) tuple that matches an existing
 * message record.
 */
export const resendDriverDeeplink = (context: string, identity: string) => {
  if (!context || !identity) {
    console.error(`resendDriverDeeplink missing 'context' or 'identity' args`)
    return
  }
  return apis.resendDeeplink(context, identity)
}
